import React, { useEffect, useRef, useState } from 'react'
import {
  CardCvcElement,
  CardExpiryElement,
  CardNumberElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { Buttons, Disclaimer, Form, Input, InputWrapper, Row } from './styles'
import useTheme from '@mui/material/styles/useTheme'
import * as yup from 'yup'
import {
  usePatientFinancePaymentMethods,
  usePaymentPlan,
  useStripeClientSecret,
  usePaymentMethods,
} from '@/v2/hooks/usePatientFinance'
import { usePaymentMethodRefetch } from '@/v2/hooks/usePaymentMethodRefetch'
import { yupResolver } from '@hookform/resolvers/yup'
import { useForm } from 'react-hook-form'
import Typography from '@/v2/components/common/Typography'
import Label from '@/v2/components/common/FieldLabel'
import ActionButton from '@/v2/components/common/ActionButton'
import FormControl from '@mui/material/FormControl'
import FormErrorMessage from '@/v2/components/common/FormErrorMessage'
import Modal from '@/v2/components/common/Modal'
import Box from '@mui/material/Box'
import { useSnackbarContext } from '@/v2/contexts/SnackbarContext'

const schema = yup
  .object({
    zipcode: yup
      .string()
      .nullable()
      .max(5, 'Zipcode is too long'),
    cardNumberValid: yup.boolean().required('Card is required'),
    cardExpiryValid: yup.boolean().required('Card expiry is required'),
    cardCvcValid: yup.boolean().required('Card CVC is required'),
  })
  .test('cardNumberValid', 'Card is invalid', function(value) {
    const { cardNumberValid, cardExpiryValid, cardCvcValid } = value
    return cardNumberValid && cardExpiryValid && cardCvcValid
  })
  .test('zipcode', 'Zipcode is invalid', function(value) {
    const { zipcode } = value
    if (zipcode && zipcode.length > 0 && zipcode.length < 5) {
      return false
    }
    return true
  })

// Months since year zero of an M/YYYY expiration, to compare two of them.
const expirationMonths = expiration => {
  const [month, year] = expiration.split('/').map(Number)
  return year * 12 + month
}

const CardForm = ({ patientId, backButton, onSuccess, reverse = false }) => {
  const theme = useTheme()
  const stripe = useStripe()
  const elements = useElements()
  const { createClientSecret } = useStripeClientSecret()
  const { refetch, creditCards } = usePatientFinancePaymentMethods({
    patientId,
  })
  const { checkDuplicatePaymentPlan, renewCardExpiration } = usePaymentPlan()
  const { updateInvoicesPaymentMethod } = usePaymentMethods()
  const { refetchPaymentMethodQueries } = usePaymentMethodRefetch(patientId)
  const [isFirstCard, setIsFirstCard] = useState(false)
  // { expiration, keptExpiration, resolve } while the renewal prompt is open.
  // keptExpiration is set when the prompt restores an archived card instead.
  const [renewalPrompt, setRenewalPrompt] = useState(null)
  // What the last submit did, until the success effect reports it. The effect
  // runs again whenever a callback it uses changes identity while
  // isSubmitSuccessful is still true, so it reports each submit only once.
  const outcomeRef = useRef(null)
  const { onOpenSnackbar } = useSnackbarContext()

  const {
    handleSubmit,
    register,
    setValue,
    setError,
    reset,
    formState: { errors, isSubmitting, isSubmitSuccessful },
  } = useForm({
    mode: 'onSubmit',
    resolver: yupResolver(schema),
    defaultValues: {
      zipcode: '',
    },
  })

  useEffect(() => {
    const outcome = outcomeRef.current
    if (!isSubmitSuccessful || !outcome) return

    outcomeRef.current = null
    if (onSuccess) {
      onSuccess(outcome)
    }
    refetch()
    refetchPaymentMethodQueries()
    elements.getElement(CardNumberElement).clear()
    elements.getElement(CardExpiryElement).clear()
    elements.getElement(CardCvcElement).clear()
    reset()
  }, [
    isSubmitSuccessful,
    onSuccess,
    refetch,
    refetchPaymentMethodQueries,
    reset,
    elements,
  ])

  // Resolves true when the user confirms the renewal, false when they cancel.
  const askToRenewExpiration = (expiration, keptExpiration) =>
    new Promise(resolve =>
      setRenewalPrompt({ expiration, keptExpiration, resolve })
    )

  const closeRenewalPrompt = confirmed => {
    renewalPrompt?.resolve(confirmed)
    setRenewalPrompt(null)
  }

  const onSubmit = async data => {
    outcomeRef.current = { renewed: false, restored: false }
    try {
      if (!stripe) return

      const createTokenRes = await stripe.createToken(
        elements.getElement(CardNumberElement),
        {
          currency: 'usd',
        }
      )

      const { token, error: tokenError } = createTokenRes

      if (tokenError) {
        console.error('[CardForm] Token error:', tokenError)
        throw new Error(tokenError.message)
      }

      if (token) {
        const {
          duplicate,
          expirationUpdatable,
          archived,
          savedExpirationDate,
        } = await checkDuplicatePaymentPlan({
          patientId,
          tokenId: token.id,
        })

        // Reissued card (same number, later expiration) or archived card: renew
        // or restore the saved copy instead of adding a new one, and stop before
        // the SetupIntent, which would attach a second copy of the card. An
        // archived card whose saved expiration is not earlier keeps it, so its
        // prompt offers the restore instead.
        if (duplicate && expirationUpdatable) {
          const expiration = `${token.card.exp_month}/${token.card.exp_year}`
          const keepsSavedExpiration =
            archived &&
            !!savedExpirationDate &&
            expirationMonths(expiration) <=
              expirationMonths(savedExpirationDate)
          const confirmed = await askToRenewExpiration(
            expiration,
            keepsSavedExpiration ? savedExpirationDate : null
          )

          if (!confirmed) {
            // Any error keeps react-hook-form from treating the submit as a
            // success, which would clear the card and close the form. Nothing
            // renders this one.
            return setError('root.renewalCanceled', { type: 'manual' })
          }

          const paymentMethods = await renewCardExpiration({
            patientId,
            tokenId: token.id,
          })
          // What the update did, not what the prompt offered: an archived card
          // counts as restored unless the update moved it to the new date.
          const restored =
            !!archived &&
            (expiration === savedExpirationDate ||
              !paymentMethods?.some(
                method => method.expirationDate === expiration
              ))
          outcomeRef.current = { renewed: true, restored }
          onOpenSnackbar(
            restored ? 'Card restored' : 'Card expiration updated',
            'success'
          )
          return
        }

        if (duplicate) {
          console.warn('[CardForm] Duplicate card detected')
          throw new Error(
            'You already have this credit card. Please request an administrator to process this request.'
          )
        }

        const resClientSecret = await createClientSecret({
          patientId,
        })
        const { clientSecret } = resClientSecret
        if (!clientSecret) {
          console.error('[CardForm] No clientSecret received')
          return
        }

        const res = await stripe.confirmCardSetup(clientSecret, {
          payment_method: {
            card: elements.getElement(CardNumberElement),
            billing_details: {
              address: {
                postal_code: data.zipcode,
              },
            },
          },
        })

        if (res.error) {
          console.error('[CardForm] confirmCardSetup error:', res.error)
          throw new Error(res.error.message)
        }

        await updateInvoicesPaymentMethod({
          patientId: patientId,
        })

        refetchPaymentMethodQueries()

        onOpenSnackbar('Card added successfully!', 'success')

        return res
      }
      if (createTokenRes.error) {
        console.error('[CardForm] createTokenRes.error:', createTokenRes.error)
        throw new Error(createTokenRes.error)
      }
      throw new Error(
        'Payment processing failed. Please check your card details and try again.'
      )
    } catch (error) {
      console.error('[CardForm] Caught error in onSubmit:', error)
      onOpenSnackbar(error.message, 'error')
      return setError('root.clientSecret', {
        type: 'manual',
        message: error.message,
      })
    }
  }

  const handleChange = (event, fieldName) => {
    if (event?.complete) {
      return setValue(fieldName, true)
    }
    return setValue(fieldName, false)
  }

  const onSubmitEvent = e => {
    e.preventDefault()
    e.stopPropagation()

    if (creditCards.length === 0) {
      setIsFirstCard(true)
      return
    }

    handleSubmit(onSubmit)()
  }

  const handleConfirmFirstCard = () => {
    setIsFirstCard(false)
    handleSubmit(onSubmit)()
  }

  const handleCancelFirstCard = () => {
    setIsFirstCard(false)
  }

  return (
    <>
      <Form onSubmit={onSubmitEvent} theme={theme.palette}>
        <FormErrorMessage errors={errors} name="root.clientSecret" />

        <FormControl variant="standard">
          <Label>Card number</Label>
          <InputWrapper $error={!!errors.cardNumberValid}>
            <CardNumberElement
              onReady={e => e.focus()}
              onChange={e => handleChange(e, 'cardNumberValid')}
            />
          </InputWrapper>
          {errors.cardNumberValid && (
            <Typography variant="error">
              {errors.cardNumberValid.message}
            </Typography>
          )}
        </FormControl>

        <Row>
          <FormControl variant="standard">
            <Label>Expires</Label>
            <InputWrapper $error={!!errors.cardExpiryValid}>
              <CardExpiryElement
                onChange={e => handleChange(e, 'cardExpiryValid')}
              />
            </InputWrapper>
            {errors.cardExpiryValid && (
              <Typography variant="error">
                {errors.cardExpiryValid.message}
              </Typography>
            )}
          </FormControl>
          <FormControl variant="standard">
            <Label>CVC</Label>
            <InputWrapper $error={!!errors.cardCvcValid}>
              <CardCvcElement
                onChange={e => handleChange(e, 'cardCvcValid')}
                options={{ placeholder: '123' }}
              />
            </InputWrapper>
            {errors.cardCvcValid && (
              <Typography variant="error">
                {errors.cardCvcValid.message}
              </Typography>
            )}
          </FormControl>
          <FormControl variant="standard">
            <Label>Zipcode</Label>
            <Input
              id={'zipcode'}
              {...register('zipcode')}
              $error={!!errors.zipcode}
              helperText={errors.zipcode?.message}
              placeholder={'20191'}
            />
          </FormControl>
        </Row>

        <Disclaimer theme={theme.palette}>
          By saving your card details, you authorize Smilebar to save your
          information for future purchases, subscriptions and renewals.
        </Disclaimer>

        <Buttons $reverse={reverse} theme={theme.palette}>
          {backButton ? backButton() : null}
          <ActionButton
            className="text-background"
            color="secondary"
            variant="contained"
            disabled={isSubmitting}
            loading={isSubmitting}
            type="submit"
          >
            Add Card
          </ActionButton>
        </Buttons>
      </Form>

      {renewalPrompt && (
        <Modal
          isOpen={true}
          title={
            renewalPrompt.keptExpiration
              ? 'This card was archived'
              : 'This card is already saved'
          }
          confirmLabel={
            renewalPrompt.keptExpiration ? 'Restore card' : 'Update expiration'
          }
          cancelLabel="Cancel"
          onConfirm={() => closeRenewalPrompt(true)}
          onClose={() => closeRenewalPrompt(false)}
          onCancel={() => closeRenewalPrompt(false)}
          maxWidth="sm"
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="body1" marginBottom="24">
              {renewalPrompt.keptExpiration
                ? `Restore it? It keeps its saved expiration, ${renewalPrompt.keptExpiration}.`
                : `Update its expiration to ${renewalPrompt.expiration}? The payment plans that use this card will keep charging it.`}
            </Typography>
          </Box>
        </Modal>
      )}

      {isFirstCard && (
        <Modal
          isOpen={true}
          title="Set Default Payment Method"
          confirmLabel="Continue"
          cancelLabel="Cancel"
          onConfirm={handleConfirmFirstCard}
          onClose={handleCancelFirstCard}
          onCancel={handleCancelFirstCard}
          maxWidth="sm"
        >
          <Box sx={{ textAlign: 'center' }}>
            <Typography variant="h4" fontSize="18px" marginBottom="16">
              This will be the patient's first payment method
            </Typography>
            <Typography variant="body1" marginBottom="24">
              Since this is the first card, it will automatically be set as the
              default payment method for future transactions, subscriptions, and
              renewals.
            </Typography>
          </Box>
        </Modal>
      )}
    </>
  )
}

export default CardForm
